/**
 * Theme Validation — sanitize tenant-supplied theme config before it ever
 * reaches applyTheme()/`:root`.
 *
 * EAA-1664: tenant-editable theme is an injection surface — a tenant admin's
 * settings-screen input reaches every visitor's browser as CSS custom
 * properties. This module whitelists known token keys and constrains values
 * to color/URL/font patterns so a tenant can never smuggle raw CSS (e.g. a
 * `url(...)`-based data exfiltration, `expression()`, or a value containing
 * `;`/`}` that breaks out of a declaration) through the theme cascade.
 */

import type {
    CustomThemeColors,
    CustomThemeConfig,
    CustomThemeRadius,
    CustomThemeSkin,
    CustomThemeSkinTokens,
    CustomThemeTypography,
} from './types';

const THEME_COLOR_KEYS: (keyof CustomThemeColors)[] = [
    'primary',
    'cta',
    'background',
    'surface',
    'text',
    'textMuted',
    'border',
    'sidebarSelectedBg',
    'sidebarSelectedText',
    'surfaceElevated',
    'textLight',
    'textOnCta',
    // EAA-1804: table header is tenant-brandable on purpose now, not as a
    // side effect of `cta` — so both keys need to pass the whitelist.
    'tableHeaderBg',
    'tableHeaderText',
    'success',
    'warning',
    'error',
    'info',
];

const THEME_RADIUS_KEYS: (keyof CustomThemeRadius)[] = ['sm', 'md', 'lg', 'xl', 'full'];

// EAA-1664 fix: hexToHSL/darkenHex/lightenHex in theme-runtime.ts all
// regex-parse assuming exactly 6 hex digits, no alpha — but Ant Design's
// `ColorPicker.toHexString()` (used by ThemeEditorPage) emits **8-digit**
// hex whenever a color has any transparency, and 3-digit shorthand is valid
// CSS too. Rejecting those outright (an earlier version of this fix did)
// silently dropped real tenant-saved colors — if enough of a tenant's
// palette used alpha, `sanitizeThemeConfig` returned null for the whole
// config, i.e. "tenant theme doesn't load". Accept all three forms and
// normalize to 6-digit hex (dropping the alpha channel, which the hex/HSL
// pipeline has no concept of) so downstream conversions stay correct.
const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** A CSS length value — used for border-radius overrides (e.g. `8px`, `0.5rem`, `9999px`). */
const CSS_LENGTH_PATTERN = /^\d+(?:\.\d+)?(?:px|rem|em|%)$/i;

/** A safe font-family value: letters, digits, spaces, commas, hyphens, quotes — no `{`, `}`, `;`, `<`, `>`. */
const FONT_FAMILY_PATTERN = /^[a-z0-9 ,'"-]+$/i;

export function isValidThemeColorValue(value: unknown): value is string {
    if (typeof value !== 'string' || value.length === 0 || value.length > 64) return false;
    return HEX_COLOR_PATTERN.test(value);
}

/**
 * Normalize a validated hex color to the canonical 6-digit `#rrggbb` form
 * the rest of the runtime (hexToHSL/darkenHex/lightenHex) expects — expands
 * 3-digit shorthand, strips an 8-digit value's alpha channel. Returns null
 * if `value` doesn't pass `isValidThemeColorValue`.
 */
export function normalizeThemeColorValue(value: unknown): string | null {
    if (!isValidThemeColorValue(value)) return null;
    const digits = value.slice(1).toLowerCase();
    if (digits.length === 3) {
        return `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`;
    }
    return `#${digits.slice(0, 6)}`;
}

export function isValidLogoUrl(value: unknown): value is string {
    if (typeof value !== 'string' || value.length === 0 || value.length > 2048) return false;
    // Only allow absolute https URLs or root-relative paths — never `javascript:`, `data:`, or protocol-relative URLs.
    return /^https:\/\//i.test(value) || /^\/[^/\\]/.test(value);
}

// EAA-1671: exported so other packages that resolve theme-shaped config
// values reaching CSS custom properties (e.g. content-core's per-item theme
// resolver) can reuse the same choke-point patterns instead of duplicating
// them — a persisted `borderRadius`/`fontFamily` is exactly as untrusted as
// the tenant `borderRadius`/`typography.fontFamily` these were written for.
export function isValidCssLengthValue(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0 && value.length <= 32 && CSS_LENGTH_PATTERN.test(value);
}

export function isValidFontFamilyValue(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        value.length > 0 &&
        value.length <= 200 &&
        FONT_FAMILY_PATTERN.test(value)
    );
}

function sanitizeColorMap(overrides: Partial<CustomThemeColors> | undefined): Partial<CustomThemeColors> {
    if (!overrides) return {};
    const sanitized: Partial<CustomThemeColors> = {};
    for (const key of THEME_COLOR_KEYS) {
        const normalized = normalizeThemeColorValue(overrides[key]);
        if (normalized !== null) {
            sanitized[key] = normalized;
        }
    }
    return sanitized;
}

function sanitizeRadius(radius: CustomThemeRadius | undefined): CustomThemeRadius | undefined {
    if (!radius) return undefined;
    const sanitized: CustomThemeRadius = {};
    for (const key of THEME_RADIUS_KEYS) {
        const value = radius[key];
        if (typeof value === 'string' && CSS_LENGTH_PATTERN.test(value)) {
            sanitized[key] = value;
        }
    }
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

function sanitizeTypography(
    typography: CustomThemeTypography | undefined
): CustomThemeTypography | undefined {
    if (!typography) return undefined;
    const { fontFamily } = typography;
    if (typeof fontFamily !== 'string' || fontFamily.length === 0 || fontFamily.length > 200) {
        return undefined;
    }
    if (!FONT_FAMILY_PATTERN.test(fontFamily)) return undefined;
    return { fontFamily };
}

// EAA-1675: the tenant layer is runtime-injected and `skin` is stamped
// straight onto a DOM attribute (`data-skin`) — allow-list to the known
// skin ids rather than passing an arbitrary string through.
function sanitizeSkin(skin: unknown): CustomThemeSkin | undefined {
    return skin === 'playful' ? 'playful' : undefined;
}

/** A bare CSS length/time value — used for skinTokens depth/width/motion. */
const SKIN_TOKEN_LENGTH_PATTERN = /^\d+(?:\.\d+)?(?:px|rem|em|ms)$/i;

// EAA-1675: skinTokens.shadowRaised/fontDisplay are bounded free text (not a
// fixed pattern like a length or hex color). A `;`/`{`/`}` blocklist alone
// is bypassable: CSS unescapes backslash sequences before parsing, so
// `\75rl(...)` tokenizes as `url(...)` without ever containing the literal
// substring — so this instead allow-lists the character set a legitimate
// shadow/font value needs (letters, digits, common punctuation used by
// `hsl()`/`var()`/quoted font names) and rejects backslash outright, which
// kills escape-sequence smuggling regardless of what it would decode to.
// The function-name check on top blocks unescaped `url()`/`src()`/
// `image-set()` — CSS's remote-fetch surface — even though none of those
// characters would fail the allow-list on their own.
const CSS_SKIN_TOKEN_SAFE_CHARS_PATTERN = /^[a-z0-9 ,.'"()#%+/-]*$/i;
const CSS_REMOTE_FETCH_FUNCTION_PATTERN = /\b(?:url|src|image-set|cross-fade|element)\s*\(/i;

function isValidSkinTokenLength(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0 && value.length <= 32 && SKIN_TOKEN_LENGTH_PATTERN.test(value);
}

function isValidSkinTokenFreeText(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        value.length > 0 &&
        value.length <= 200 &&
        CSS_SKIN_TOKEN_SAFE_CHARS_PATTERN.test(value) &&
        !CSS_REMOTE_FETCH_FUNCTION_PATTERN.test(value)
    );
}

function sanitizeSkinTokens(
    tokens: CustomThemeSkinTokens | undefined
): CustomThemeSkinTokens | undefined {
    if (!tokens) return undefined;
    const sanitized: CustomThemeSkinTokens = {};
    if (isValidSkinTokenLength(tokens.btnDepth)) sanitized.btnDepth = tokens.btnDepth;
    if (isValidSkinTokenLength(tokens.borderWidthUi)) sanitized.borderWidthUi = tokens.borderWidthUi;
    if (isValidSkinTokenLength(tokens.motionPress)) sanitized.motionPress = tokens.motionPress;
    if (isValidSkinTokenFreeText(tokens.shadowRaised)) sanitized.shadowRaised = tokens.shadowRaised;
    if (isValidSkinTokenFreeText(tokens.fontDisplay)) sanitized.fontDisplay = tokens.fontDisplay;
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/**
 * Whitelist tenant-supplied theme config down to known keys and valid value
 * patterns. Unknown keys and malformed values are dropped, not rejected
 * wholesale — a partially-valid tenant config should still apply its valid
 * fields rather than fail closed to nothing.
 */
export function sanitizeThemeConfig(
    config: CustomThemeConfig | null | undefined
): CustomThemeConfig | null {
    if (!config) return null;

    const light = sanitizeColorMap(config.light);
    const dark = sanitizeColorMap(config.dark);
    const logoUrl = isValidLogoUrl(config.logoUrl) ? config.logoUrl : undefined;
    const borderRadius = sanitizeRadius(config.borderRadius);
    const typography = sanitizeTypography(config.typography);
    const skin = sanitizeSkin(config.skin);
    const skinTokens = sanitizeSkinTokens(config.skinTokens);

    const hasAnything =
        Object.keys(light).length > 0 ||
        Object.keys(dark).length > 0 ||
        logoUrl !== undefined ||
        borderRadius !== undefined ||
        typography !== undefined ||
        skin !== undefined ||
        skinTokens !== undefined;

    if (!hasAnything) return null;

    return { light, dark, logoUrl, borderRadius, typography, skin, skinTokens };
}
